import {useState} from "react";
import {
    Dialog, DialogContent, DialogTitle, Box, Stack, Typography,
    TextField, Button, Chip, IconButton,
} from "@mui/material";
import {IconServer, IconX} from "@tabler/icons-react";
import {toast} from "react-toastify";
import {KnowledgeDatabaseRepository} from "../../../../repositories/KnowledgeDatabaseRepository";

const COLORS = [
    "#6366f1", "#8b5cf6", "#ec4899", "#f43f5e", "#f97316",
    "#eab308", "#22c55e", "#14b8a6", "#06b6d4", "#3b82f6",
];

const inputSx = {
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#000",
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#000",
    },
};

export const CreateTopicModal = ({open, onClose, onCreated, project}) => {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [host, setHost] = useState("localhost");
    const [port, setPort] = useState("9621");
    const [apiKey, setApiKey] = useState("");
    const [workingDir, setWorkingDir] = useState("/data/lightrag/");
    const [color, setColor] = useState(COLORS[0]);
    const [tagInput, setTagInput] = useState("");
    const [tags, setTags] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleAddTag = () => {
        const tag = tagInput.trim().toLowerCase();
        if (tag && !tags.includes(tag)) {
            setTags([...tags, tag]);
            setTagInput("");
        }
    };

    const resetForm = () => {
        setName("");
        setDescription("");
        setHost("localhost");
        setPort("9621");
        setApiKey("");
        setWorkingDir("/data/lightrag/");
        setColor(COLORS[0]);
        setTagInput("");
        setTags([]);
    };

    const handleSubmit = async () => {
        if (!name.trim() || isSubmitting) return;

        setIsSubmitting(true);
        try {
            const portNum = parseInt(port) || 9621;
            const topic = await KnowledgeDatabaseRepository.create({
                name: name.trim(),
                description: description.trim(),
                tags,
                color,
                server: {
                    host,
                    port: portNum,
                    apiBase: `http://${host}:${portNum}`,
                    apiKey: apiKey || undefined,
                    workingDir: workingDir || undefined,
                },
                organization: project?.settings?.organization
            });
            toast.success(`Topic "${topic.name}" created`);
            resetForm();
            onCreated?.();
            onClose();
        } catch (err) {
            toast.error(err.message ?? "Failed to create topic");
        } finally {
            setIsSubmitting(false);
        }
    };

    const portNum = parseInt(port) || 9621;

    return (
        <Dialog open={open} onClose={onClose} maxWidth={"sm"} fullWidth>
            <DialogTitle sx={{fontWeight: 700, fontSize: 17}}>Create New Topic</DialogTitle>
            <DialogContent>
                <Stack gap={2.5} sx={{mt: 1}}>

                    <Box>
                        <Typography fontSize={13} sx={{mb: 0.75}}>Topic Name *</Typography>
                        <TextField
                            fullWidth size={"small"}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={"e.g., Product Support"}
                            sx={inputSx}
                        />
                    </Box>

                    <Box>
                        <Typography fontSize={13} sx={{mb: 0.75}}>Description</Typography>
                        <TextField
                            fullWidth size={"small"} multiline rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={"Describe what this knowledge base will cover..."}
                            sx={inputSx}
                        />
                    </Box>

                    <Box>
                        <Typography fontSize={13} sx={{mb: 0.75}}>Color</Typography>
                        <Stack direction={"row"} gap={1}>
                            {COLORS.map(c => (
                                <Box
                                    key={c}
                                    onClick={() => setColor(c)}
                                    sx={{
                                        width: 28, height: 28, borderRadius: "50%", bgcolor: c, cursor: "pointer",
                                        outline: color === c ? "2px solid" : "none",
                                        outlineColor: "text.primary",
                                        outlineOffset: "2px",
                                        transform: color === c ? "scale(1.1)" : "scale(1)",
                                        transition: "transform 0.15s",
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    <Box>
                        <Typography fontSize={13} sx={{mb: 0.75}}>Tags</Typography>
                        <Stack direction={"row"} gap={1}>
                            <TextField
                                fullWidth size={"small"}
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                placeholder={"Add a tag..."}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleAddTag();
                                    }
                                }}
                                sx={inputSx}
                            />
                            <Button
                                variant={"modal"}
                                onClick={handleAddTag}
                                sx={{
                                    textTransform: "none",
                                    color: "text.primary",
                                    borderColor: "divider",
                                    "&:hover": {borderColor: "text.secondary", bgcolor: "action.hover"},
                                }}
                            >
                                Add
                            </Button>
                        </Stack>
                        {tags.length > 0 && (
                            <Stack direction={"row"} gap={1} flexWrap={"wrap"} sx={{mt: 1}}>
                                {tags.map(tag => (
                                    <Chip
                                        key={tag} label={tag} size={"small"}
                                        onDelete={() => setTags(tags.filter(t => t !== tag))}
                                        sx={{fontSize: 11}}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Box>

                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                        <IconServer size={16}/>
                        <Typography fontWeight={600} fontSize={14}>LightRAG Server Configuration</Typography>
                    </Stack>

                    <Stack direction={"row"} gap={2}>
                        <Box sx={{flex: 1}}>
                            <Typography fontSize={13} sx={{mb: 0.75}}>Host</Typography>
                            <TextField
                                fullWidth size={"small"} value={host}
                                onChange={(e) => setHost(e.target.value)}
                                placeholder={"localhost"}
                                sx={inputSx}
                            />
                        </Box>
                        <Box sx={{flex: 1}}>
                            <Typography fontSize={13} sx={{mb: 0.75}}>Port</Typography>
                            <TextField
                                fullWidth size={"small"} type={"number"} value={port}
                                onChange={(e) => setPort(e.target.value)}
                                placeholder={"9621"}
                                sx={inputSx}
                            />
                        </Box>
                    </Stack>

                    <Box>
                        <Typography fontSize={13} sx={{mb: 0.75}}>API Key (optional)</Typography>
                        <TextField
                            fullWidth size={"small"} type={"password"} value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            placeholder={"lrag-key-xxxxx"}
                            sx={inputSx}
                        />
                        <Typography fontSize={11} color={"text.secondary"} sx={{mt: 0.5}}>
                            Stored encrypted — never readable after saving
                        </Typography>
                    </Box>

                    <Box>
                        <Typography fontSize={13} sx={{mb: 0.75}}>Working Directory</Typography>
                        <TextField
                            fullWidth size={"small"} value={workingDir}
                            onChange={(e) => setWorkingDir(e.target.value)}
                            placeholder={"/data/lightrag/my-topic"}
                            sx={inputSx}
                        />
                    </Box>

                    <Box sx={{bgcolor: "action.hover", borderRadius: 1.5, p: 1.5}}>
                        <Typography fontSize={12} color={"text.secondary"}>
                            <strong>API Base URL:</strong>{" "}
                            <Box component={"code"} sx={{bgcolor: "action.selected", px: 0.5, borderRadius: 0.5}}>
                                http://{host}:{portNum}
                            </Box>
                        </Typography>
                    </Box>

                    <Stack direction={"row"} justifyContent={"flex-end"} gap={1.5} sx={{mt: 1}}>
                        <Button
                            onClick={onClose}
                            sx={{
                                textTransform: "none",
                                color: "text.secondary",
                                "&:hover": {bgcolor: "action.hover"},
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant={"modal"}
                            disabled={!name.trim() || isSubmitting}
                            onClick={handleSubmit}
                            sx={{
                                bgcolor: "#000", color: "#fff", textTransform: "none", borderRadius: 2,
                                boxShadow: "none", "&:hover": {bgcolor: "#000"},
                            }}
                        >
                            {isSubmitting ? "Creating..." : "Create Topic"}
                        </Button>
                    </Stack>

                </Stack>
            </DialogContent>
        </Dialog>
    );
};